import { useState, useEffect, useRef, useMemo } from 'react';
import type { ScimEntityService } from '../services/ScimEntityService';
import {
    getSettableAttributeIdsFromTargetResponse,
    getReadableAttributeIdsFromTargetResponse,
    getIndexedReadableAttributeIdsFromTargetResponse,
    getNonIndexedReadableAttributeNamesFromTargetResponse,
    getNonIndexedHasLengthCountAttributeNamesFromTargetResponse,
    getNonIndexedHasLengthCountAttributeDisplayNamesForEagerLoad,
    getLargeArrayRefAttributeNamesFromTargetResponse,
    getLargeArrayRefAttributeDisplayNamesToExcludeFromEagerLoad,
    getDeferredArrayRefAttributeNamesFromTargetResponse,
    buildFormAttributesFromTargetEntitlements,
    buildFormAttributesFromTargetEntitlementsReadable,
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    buildFormAttributesFromEntityKeys,
    getTier2AttributesFromResponse,
    attributeIdToName,
    getTargetEntitlementsList,
    isEntitySetFacade,
    getReadOnlyAttributeNamesFromTier2,
    type EntityEntitlementForTargetResponse,
    type EntityEntitlementForSchemaResponse,
    type FormAttributeOption,
} from '../types/EntityEntitlements';
import { extractReferenceId, extractReferenceDisplayName } from '../utils/referenceUtils';
import { createEntityRef, type EntityRef } from '../types';
import {
    dedupeAttributeNamesForRequest,
    entityHasAttributesRequestedStamp,
    isEntityMissingAnyRequestedAttributes,
    NON_INDEXED_PROPERTY_STUB,
    isARef,
} from '../utils/entityPropertyUtils';
import {
    sortAttributesForForm,
    filterEditableAttributes,
    type SchemaAttribute,
} from '../utils/schemaAttributeUtils';
import { SET_SCHEMA_ATTRIBUTES, entityToSetFormData } from '../utils/setSchemaUtils';
import type { ScimEntity } from '../types/ScimEntity';

export type SchemaEntityBindingsMap = Record<
    string,
    { allowedValues: any[]; required: boolean; regex?: string; readOnly?: boolean }
>;

/** Side effects runInit applies after building schema + initial form values. */
export interface UseEntityFormSchemaInitActions {
    setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    setOriginalData: React.Dispatch<React.SetStateAction<Record<string, any> | null>>;
    setFullEntityData: React.Dispatch<React.SetStateAction<any>>;
    setInitialReferenceEntitiesFromEntity: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    setMultiValuedRawInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    rawEntitySnapshotRef: React.MutableRefObject<Record<string, any> | null>;
    fetchTargetSetFilterScope: (setId: string) => Promise<void>;
}

export interface UseEntityFormSchemaParams {
    open: boolean;
    entityType: string;
    entityId: string | null | undefined;
    isEditMode: boolean;
    existingEntity: ScimEntity | null;
    entityTypeFieldOptions: FormAttributeOption[] | any[];
    targetEntitlement: EntityEntitlementForTargetResponse | null;
    targetEntitlementProp?: EntityEntitlementForTargetResponse | null;
    tier3Settled: boolean;
    tier3ResultRef: React.MutableRefObject<EntityEntitlementForTargetResponse | null>;
    entityService: ScimEntityService;
    showFieldDebugInfo: boolean;
    formKeyFromAttrName: (n: string) => string;
    isRequestApprovalType: boolean;
    isPluginFunction: boolean;
    isContent: boolean;
    isEmailTemplate: boolean;
    isFile: boolean;
    isPolicyRule: boolean;
    isSet: boolean;
    isSetTransition: boolean;
    /** When true (unified inline screen), wait for parent detail loader; do not call getEntityById here. */
    parentProvidesEntityLoad?: boolean;
    init: UseEntityFormSchemaInitActions;
}

/** Schema load/init for GenericEntityUpsertDialog (tier2/tier3 attributes + form bootstrap). */
export function useEntityFormSchema({
    open,
    entityType,
    entityId,
    isEditMode,
    existingEntity,
    entityTypeFieldOptions,
    targetEntitlement,
    targetEntitlementProp,
    tier3Settled,
    tier3ResultRef,
    entityService,
    showFieldDebugInfo,
    formKeyFromAttrName,
    isRequestApprovalType,
    isPluginFunction,
    isContent,
    isEmailTemplate,
    isFile,
    isPolicyRule,
    isSet,
    isSetTransition,
    parentProvidesEntityLoad = false,
    init,
}: UseEntityFormSchemaParams) {
    const {
        setFormData,
        setOriginalData,
        setFullEntityData,
        setInitialReferenceEntitiesFromEntity,
        setMultiValuedRawInputs,
        setError,
        rawEntitySnapshotRef,
        fetchTargetSetFilterScope,
    } = init;

    const [schemaAttributes, setSchemaAttributes] = useState<any[]>([]);
    const [schemaLoading, setSchemaLoading] = useState(true);
    const [schemaEntityBindings, setSchemaEntityBindings] = useState<SchemaEntityBindingsMap>({});

    const schemaLoadKeyRef = useRef<string>('');
    const isInitializingRef = useRef<boolean>(false);
    const entityTypeFieldOptionsRef = useRef<any[]>([]);
    const lastEntityTypeRef = useRef<string>('');
    const existingEntityRef = useRef<ScimEntity | null>(null);
    const hasInitializedRef = useRef<boolean>(false);
    const entityFetchIdRef = useRef(0);

    const existingEntityShapeKey = useMemo(() => {
        if (!existingEntity || typeof existingEntity !== 'object' || Array.isArray(existingEntity)) {
            return 'none';
        }
        return Object.keys(existingEntity as object).sort().join(',');
    }, [existingEntity]);

    const existingEntityBodyKey =
        existingEntity && typeof existingEntity === 'object' && !Array.isArray(existingEntity)
            ? (existingEntity as Record<string, unknown>).Body ??
              (existingEntity as Record<string, unknown>).body
            : undefined;
    const existingEntityContentTypeKey =
        existingEntity && typeof existingEntity === 'object' && !Array.isArray(existingEntity)
            ? (existingEntity as Record<string, unknown>).ContentType ??
              (existingEntity as Record<string, unknown>).contentType
            : undefined;

    useEffect(() => {
        if (!open) {
            setSchemaLoading(true);
            setSchemaAttributes([]);
            setSchemaEntityBindings({});
        } else {
            setSchemaLoading(true);
            setSchemaAttributes([]);
        }
    }, [open]);

    useEffect(() => {
        existingEntityRef.current = existingEntity;
    }, [existingEntity, existingEntityShapeKey, existingEntityBodyKey, existingEntityContentTypeKey]);

    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            schemaLoadKeyRef.current = '';
            isInitializingRef.current = false;
            lastEntityTypeRef.current = '';
        }
    }, [open]);

    // Load schema attributes and existing entity data
    useEffect(() => {
        if (!open) {
            return;
        }

        if (!entityType) {
            return;
        }

        if (isInitializingRef.current) {
            return;
        }

        const currentFieldOptions = entityTypeFieldOptions;
        const hasFieldOptions = currentFieldOptions && currentFieldOptions.length > 0;
        const tier3ForGuard = targetEntitlement ?? tier3ResultRef.current;
        const canBuildFromTier3 = isEditMode && getTargetEntitlementsList(tier3ForGuard ?? null).length > 0;
        const canBuildFromTier2 = !isEditMode && !!entityType?.trim();
        if (!hasFieldOptions && !canBuildFromTier3 && !canBuildFromTier2) {
            setSchemaLoading(false);
            return;
        }

        if (isEditMode && !tier3Settled && targetEntitlementProp === undefined) {
            return;
        }

        if (parentProvidesEntityLoad && isEditMode && entityId) {
            if (!entityHasAttributesRequestedStamp(existingEntityRef.current)) {
                return;
            }
        }

        const entityTypeChanged = lastEntityTypeRef.current !== entityType;

        const currentExistingEntity = existingEntityRef.current;
        const existingEntityKey = currentExistingEntity ? Object.keys(currentExistingEntity).sort().join(',') : 'none';
        const loadKey = `${entityType}-${isEditMode}-${entityId || 'new'}-${existingEntityKey}`;

        if (schemaLoadKeyRef.current === loadKey && !entityTypeChanged && (hasInitializedRef.current || isInitializingRef.current)) {
            return;
        }

        entityTypeFieldOptionsRef.current = currentFieldOptions || [];

        schemaLoadKeyRef.current = loadKey;
        lastEntityTypeRef.current = entityType;
        isInitializingRef.current = true;
        const wasAlreadyInitialized = hasInitializedRef.current;
        hasInitializedRef.current = false;

        const entitlementSnapshot = targetEntitlement ?? tier3ResultRef.current;

        const loadSchema = () => {
            if (!open) {
                isInitializingRef.current = false;
                return;
            }

            setSchemaLoading(true);

            const currentExisting = existingEntityRef.current;
            if (isPolicyRule && currentExisting?.TargetSet) {
                const existingTargetSetId = extractReferenceId(currentExisting.TargetSet);
                if (existingTargetSetId) fetchTargetSetFilterScope(existingTargetSetId);
            }

            const runInit = async () => {
                try {
                    const EntitlementsServiceModule = await import('../services/EntitlementsService');
                    const EntitlementsServiceInstance = EntitlementsServiceModule.default;
                    let attributes = entityTypeFieldOptions || [];
                    let tier2ForClone: EntityEntitlementForSchemaResponse | null = null;
                    if (isEditMode && getTargetEntitlementsList(entitlementSnapshot ?? null).length > 0) {
                        attributes = buildFormAttributesFromTargetEntitlementsReadable(entitlementSnapshot);
                    }
                    const existingForAttrs = existingEntityRef.current;
                    const isStubForAttrs = !existingForAttrs || typeof existingForAttrs !== 'object' || Array.isArray(existingForAttrs) ||
                        Object.keys(existingForAttrs).filter((k) => !['Id', 'id', 'EntityType', 'entityType', 'DisplayName', 'displayName'].includes(k)).length === 0;
                    const isMetaType = entityType?.toLowerCase() === 'requestapprovaltype' || entityType?.toLowerCase() === 'policyrule' || entityType?.toLowerCase() === 'pluginfunction';
                    if (attributes.length === 0 && isEditMode && isMetaType && !isStubForAttrs && existingForAttrs && typeof existingForAttrs === 'object' && !Array.isArray(existingForAttrs)) {
                        attributes = buildFormAttributesFromEntityKeys(existingForAttrs as Record<string, unknown>);
                    }
                    if (attributes.length === 0 && isEditMode && entityType?.trim()) {
                        const schemaEntityKey = entityType.trim().toLowerCase();
                        let tier2;
                        try {
                            tier2 = await EntitlementsServiceInstance.fetchEntityEntitlementForSchemaEntity(schemaEntityKey);
                        } catch (tier2Err) {
                            console.error(`Selector entitlement (tier2 schema fallback, edit) failed for entityType "${schemaEntityKey}":`, tier2Err);
                            tier2 = null;
                        }
                        tier2ForClone = tier2;
                        const fromTier2 = isEntitySetFacade(tier2) ? buildFormAttributesFromTier2Attributes(tier2?.Attributes) : (buildFormAttributesFromTier2AttributeBindings(tier2?.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2?.Attributes));
                        if (getTargetEntitlementsList(entitlementSnapshot ?? null).length > 0 && fromTier2.length > 0) {
                            const normalizeAttrForMatch = (id: string) => (id ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                            const readableIds = getReadableAttributeIdsFromTargetResponse(entitlementSnapshot);
                            const readSet = readableIds != null
                                ? new Set([...readableIds.map(normalizeAttrForMatch), 'id', 'displayname', 'objectid'])
                                : null;
                            attributes = readSet
                                ? fromTier2.filter((attr: any) => {
                                    const raw = (attr.name || attr.id || attr.value || '').toString().trim();
                                    if (!raw) return false;
                                    return readSet.has(normalizeAttrForMatch(raw));
                                })
                                : fromTier2;
                            const hasId = attributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'Id') || isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'ObjectId'));
                            const hasDisplayName = attributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'DisplayName'));
                            if (!hasId) {
                                attributes = [{ name: 'Id', id: 'Id', value: 'Id', label: 'Id', type: 'string', required: false, multiValued: false, isReference: false, computed: false, readOnly: true } as FormAttributeOption, ...attributes];
                            }
                            if (!hasDisplayName) {
                                attributes = [{ name: 'DisplayName', id: 'DisplayName', value: 'DisplayName', label: 'Display Name', type: 'string', required: false, multiValued: false, isReference: false, computed: false } as FormAttributeOption, ...attributes];
                            }
                        } else if (attributes.length === 0) {
                            attributes = buildFormAttributesFromTargetEntitlements(entitlementSnapshot);
                        }
                    }
                    if (attributes.length === 0 && !isEditMode && entityType?.trim()) {
                        const schemaKeyCreate = entityType.trim().toLowerCase();
                        let tier2;
                        try {
                            tier2 = await EntitlementsServiceInstance.fetchEntityEntitlementForSchemaEntity(schemaKeyCreate);
                        } catch (tier2Err) {
                            console.error(`Selector entitlement (tier2 schema fallback, create) failed for entityType "${schemaKeyCreate}":`, tier2Err);
                            tier2 = null;
                        }
                        tier2ForClone = tier2;
                        attributes = isEntitySetFacade(tier2) ? buildFormAttributesFromTier2Attributes(tier2?.Attributes) : (buildFormAttributesFromTier2AttributeBindings(tier2?.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(tier2?.Attributes));
                        if (open && !isEntitySetFacade(tier2) && tier2?.AttributeBindings?.length) {
                            const bindingsMap: Record<string, { allowedValues: any[]; required: boolean; regex?: string; readOnly?: boolean }> = {};
                            for (const ab of tier2.AttributeBindings) {
                                const attr = ab.Attribute;
                                if (!attr) continue;
                                const name = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
                                if (!name) continue;
                                const b = ab.Binding;
                                const key = name.toLowerCase();
                                const allowed = b?.AllowedValues;
                                bindingsMap[key] = {
                                    allowedValues: Array.isArray(allowed) ? allowed : [],
                                    required: b?.Required === true,
                                    regex: b?.RegEx != null && String(b.RegEx).trim() !== '' ? String(b.RegEx).trim() : undefined,
                                    readOnly: (b as any)?.ReadOnly === true
                                };
                            }
                            setSchemaEntityBindings(bindingsMap);
                        }
                    }
                    if (attributes.length === 0 && isSet) {
                        attributes = SET_SCHEMA_ATTRIBUTES.map((a) => ({
                            name: a.name,
                            id: a.id,
                            value: a.id || a.name,
                            label: a.label,
                            type: a.type,
                            required: a.required ?? false,
                            multiValued: a.multiValued ?? false,
                            isReference: false,
                            computed: false
                        }));
                    }

                    entityTypeFieldOptionsRef.current = attributes;

                    const editableAttributes = filterEditableAttributes(attributes);
                    const isGroup = entityType?.toLowerCase() === 'group';
                    const isSetEntity = entityType?.toLowerCase() === 'set';
                    const sortedAttributes = sortAttributesForForm(editableAttributes, { isRequestApprovalType, isPluginFunction, isContent, isGroup, isSet: isSetEntity });

                    let formAttributes = sortedAttributes;
                    if (isEditMode && getTargetEntitlementsList(entitlementSnapshot ?? null).length > 0) {
                        const normalizeAttrForMatch = (id: string) => (id ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                        const readableIds = getReadableAttributeIdsFromTargetResponse(entitlementSnapshot);
                        const settableIds = getSettableAttributeIdsFromTargetResponse(entitlementSnapshot);
                        const settableSet = settableIds != null ? new Set(settableIds.map(normalizeAttrForMatch)) : null;
                        if (readableIds !== undefined) {
                            const readSet = new Set([...readableIds.map(normalizeAttrForMatch), 'id', 'displayname', 'objectid']);
                            formAttributes = sortedAttributes.filter((attr: any) => {
                                const raw = (attr.name || attr.id || attr.value || '').toString().trim();
                                if (!raw) return false;
                                return readSet.has(normalizeAttrForMatch(raw));
                            });
                            const hasId = formAttributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'Id') || isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'ObjectId'));
                            const hasDisplayName = formAttributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'DisplayName'));
                            if (!hasId) {
                                formAttributes = [{ name: 'Id', id: 'Id', value: 'Id', label: 'Id', type: 'string', required: false, multiValued: false, isReference: false, computed: false, readOnly: true } as SchemaAttribute, ...formAttributes];
                            }
                            if (!hasDisplayName) {
                                formAttributes = [{ name: 'DisplayName', id: 'DisplayName', value: 'DisplayName', label: 'Display Name', type: 'string', required: false, multiValued: false, isReference: false, computed: false } as SchemaAttribute, ...formAttributes];
                            }
                        }
                        if (isPolicyRule) {
                            const attributesAttr = sortedAttributes.find((a: any) => isARef(createEntityRef((a.name || a.id || a.value || '').toString().trim()), 'Attributes'));
                            const allAttributesAttr = sortedAttributes.find((a: any) => isARef(createEntityRef((a.name || a.id || a.value || '').toString().trim()), 'AllAttributes'));
                            if (attributesAttr && !formAttributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'Attributes'))) {
                                formAttributes = [...formAttributes, attributesAttr];
                            }
                            if (allAttributesAttr && !formAttributes.some((a: any) => isARef(createEntityRef((a.name || a.id || '').toString().trim()), 'AllAttributes'))) {
                                formAttributes = [...formAttributes, allAttributesAttr];
                            }
                        }
                        formAttributes = formAttributes.map((attr: SchemaAttribute): SchemaAttribute => {
                            const raw = (attr.name || attr.id || attr.value || '').toString().trim();
                            const attrRefForAttr = createEntityRef(raw);
                            const isId = isARef(attrRefForAttr, 'Id') || isARef(attrRefForAttr, 'ObjectId');
                            const name = normalizeAttrForMatch(raw);
                            const readOnly = isId || (settableSet != null && !settableSet.has(name));
                            const tier3List = getTargetEntitlementsList(entitlementSnapshot);
                            const tier3Item = tier3List.find((te: any) => {
                                const teName = normalizeAttrForMatch(te.Attribute?.Id ?? te.Attribute?.DisplayName ?? '');
                                return teName === name;
                            });
                            const isReferenceTo = tier3Item?.Attribute?.IsReferenceTo && Array.isArray(tier3Item.Attribute.IsReferenceTo) && tier3Item.Attribute.IsReferenceTo.length > 0
                                ? tier3Item.Attribute.IsReferenceTo
                                : attr.isReferenceTo;
                            return { ...attr, readOnly, ...(isReferenceTo != null && { isReferenceTo }) };
                        });
                    }

                    if (open) {
                        setSchemaAttributes(formAttributes);
                        if (getTargetEntitlementsList(entitlementSnapshot ?? null).length > 0) {
                            const bindingsMap: Record<string, { allowedValues: any[]; required: boolean; regex?: string; readOnly?: boolean }> = {};
                            for (const e of getTargetEntitlementsList(entitlementSnapshot)) {
                                const name = attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? '');
                                if (!name) continue;
                                const b = e.Binding;
                                const key = name.toLowerCase();
                                const allowed = b?.AllowedValues;
                                bindingsMap[key] = {
                                    allowedValues: Array.isArray(allowed) ? allowed : [],
                                    required: b?.Required === true,
                                    regex: b?.RegEx != null && String(b.RegEx).trim() !== '' ? String(b.RegEx).trim() : undefined,
                                    readOnly: (b as any)?.ReadOnly === true
                                };
                            }
                            setSchemaEntityBindings(bindingsMap);
                        }
                    }

                    const formKey = (n: string) => (n ?? '').toString().trim().replace(/^\$\./, '') || (n ?? '');
                    const initialData: Record<string, any> = {};
                    let initialRefsFromEntity: Record<string, any> = {};

                    const currentExisting = existingEntityRef.current;
                    let entityForInit = currentExisting;
                    if (isEditMode && entityId && entityType?.trim() && entitlementSnapshot != null) {
                        const indexedAttrs = getIndexedReadableAttributeIdsFromTargetResponse(entitlementSnapshot);
                        const nonIndexedEagerLoadDisplayNames = getNonIndexedHasLengthCountAttributeDisplayNamesForEagerLoad(entitlementSnapshot);
                        const excludeFromEager = new Set(
                            getLargeArrayRefAttributeDisplayNamesToExcludeFromEagerLoad(entitlementSnapshot).map((a) => (a ?? '').toString().trim().replace(/^\$\./, '').toLowerCase())
                        );
                        const normAttrForExclude = (a: string) => (a ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                        const rawRequestAttributes = indexedAttrs != null
                            ? [...new Set(['Id', 'DisplayName', ...(indexedAttrs || []), ...nonIndexedEagerLoadDisplayNames, ...(isContent ? ['Body'] : []), ...(isEmailTemplate ? ['Body', 'ContentType'] : []), ...(isFile ? ['ContentType'] : [])])]
                                .filter((a) => !excludeFromEager.has(normAttrForExclude(a)))
                            : (isContent ? ['Id', 'DisplayName', 'Body'] : isEmailTemplate ? ['Id', 'DisplayName', 'Body', 'ContentType'] : isFile ? ['Id', 'DisplayName', 'ContentType'] : undefined);
                        const requestAttributes = (rawRequestAttributes && rawRequestAttributes.length > 0)
                            ? dedupeAttributeNamesForRequest(rawRequestAttributes)
                            : null;
                        const needFetch = !parentProvidesEntityLoad
                            && requestAttributes != null
                            && isEntityMissingAnyRequestedAttributes(currentExisting, requestAttributes);
                        if (needFetch) {
                            const thisFetchId = ++entityFetchIdRef.current;
                            try {
                                const skipReferenceExpansion = false;
                                if (showFieldDebugInfo) {
                                    const nonIndexedNames = getNonIndexedReadableAttributeNamesFromTargetResponse(entitlementSnapshot);
                                    if (nonIndexedNames.length > 0) {
                                        console.warn(
                                            '[Edit dialog] Non-indexed readable attributes (would cause full attribute list to be requested if loaded):',
                                            nonIndexedNames
                                        );
                                    }
                                }
                                const full = await entityService.getEntityById(entityId, entityType.trim(), skipReferenceExpansion, requestAttributes);
                                if (thisFetchId === entityFetchIdRef.current && full && typeof full === 'object') {
                                    const base = entityForInit && typeof entityForInit === 'object' && !Array.isArray(entityForInit) ? { ...entityForInit } : {};
                                    entityForInit = Object.assign(base, full);
                                }
                            } catch (_e) {
                                // Keep row/ref data as fallback
                            }
                        }
                    }
                    const currentExistingEntity = entityForInit;
                    rawEntitySnapshotRef.current = (currentExistingEntity && typeof currentExistingEntity === 'object' && !Array.isArray(currentExistingEntity))
                        ? { ...currentExistingEntity }
                        : null;
                    setFullEntityData((currentExistingEntity && typeof currentExistingEntity === 'object' && !Array.isArray(currentExistingEntity))
                        ? currentExistingEntity
                        : null);

                    if (isPolicyRule && !isEditMode && !currentExistingEntity) {
                        initialData['AllAttributes'] = true;
                        initialData['ReadEntity'] = true;
                    }
                    if ((isEditMode || currentExistingEntity) && currentExistingEntity) {
                        const original: Record<string, any> = {};
                        initialRefsFromEntity = {};
                        const normalizeAttrForCloneReadOnly = (id: string) => (id ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                        const cloneReadOnlySet = currentExistingEntity && !isEditMode && tier2ForClone
                            ? new Set(getReadOnlyAttributeNamesFromTier2(tier2ForClone).map(normalizeAttrForCloneReadOnly))
                            : null;

                        if (isSet) {
                            const setForm = entityToSetFormData(currentExistingEntity);
                            Object.keys(setForm).forEach((key) => {
                                if (cloneReadOnlySet?.has(normalizeAttrForCloneReadOnly(key))) return;
                                initialData[key] = setForm[key];
                                original[key] = setForm[key];
                            });
                            const largeArrayRefNames = getLargeArrayRefAttributeNamesFromTargetResponse(entitlementSnapshot ?? null);
                            const normalizeStubKey = (id: string) => (id ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                            const formKeyNorm = (k: string) => normalizeStubKey(k);
                            largeArrayRefNames.forEach((attrName) => {
                                const key = Object.keys(initialData).find((k) => formKeyNorm(k) === normalizeStubKey(attrName)) ?? attrName;
                                initialData[key] = NON_INDEXED_PROPERTY_STUB;
                                original[key] = NON_INDEXED_PROPERTY_STUB;
                            });
                        } else {
                            const normalizeAttrForStubMatch = (id: string) => (id ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
                            const nonIndexedHasLengthCountNames = new Set([
                                ...getNonIndexedHasLengthCountAttributeNamesFromTargetResponse(entitlementSnapshot ?? null).map(normalizeAttrForStubMatch),
                                ...getLargeArrayRefAttributeNamesFromTargetResponse(entitlementSnapshot ?? null).map(normalizeAttrForStubMatch)
                            ]);
                            formAttributes.forEach((attr: any) => {
                                const name = attr.name || attr.id;
                                if (name == null || String(name).trim() === '') return;
                                const attrRef = createEntityRef(name);
                                const nameLower = name.toLowerCase();
                                const entityKey = Object.keys(currentExistingEntity).find((k) => (k ?? '').toLowerCase() === nameLower);
                                let existingValue = entityKey !== undefined ? currentExistingEntity[entityKey] : (currentExistingEntity[name] ?? currentExistingEntity[attr.label]);
                                if (cloneReadOnlySet?.has(normalizeAttrForCloneReadOnly(name))) existingValue = undefined;

                                if (isARef(attrRef, 'Body') && typeof existingValue === 'string') {
                                    const trimmed = existingValue.trim();
                                    if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
                                        try {
                                            const parsed = JSON.parse(existingValue);
                                            if (typeof parsed === 'string') {
                                                existingValue = parsed;
                                            }
                                        } catch {
                                            // keep as-is
                                        }
                                    }
                                }

                                if (isSetTransition && (isARef(attrRef, 'ActionWorkflow') || isARef(attrRef, 'TargetSet'))) {
                                    if (existingValue && typeof existingValue === 'object' && existingValue !== null) {
                                        initialRefsFromEntity[formKey(name)] = existingValue;
                                        existingValue = (existingValue as Record<string, any>).Id || (existingValue as Record<string, any>).id || (existingValue as Record<string, any>).$ref || existingValue;
                                    }
                                }

                                if ((isRequestApprovalType || isPluginFunction) && isARef(attrRef, 'PossessiveTargetAttribute')) {
                                    if (existingValue && typeof existingValue === 'object' && existingValue !== null) initialRefsFromEntity[formKey(name)] = existingValue;
                                    const attrId = extractReferenceId(existingValue);
                                    if (attrId) {
                                        existingValue = attrId;
                                    } else {
                                        existingValue = extractReferenceDisplayName(existingValue) || '';
                                    }
                                    if (existingValue && typeof existingValue === 'string') {
                                        existingValue = existingValue.trim();
                                    } else if (existingValue) {
                                        existingValue = String(existingValue);
                                    }
                                }

                                if (isRequestApprovalType && isARef(attrRef, 'PossessiveActorsAttribute')) {
                                    const singleRef = Array.isArray(existingValue) ? existingValue[0] : existingValue;
                                    if (singleRef && typeof singleRef === 'object') initialRefsFromEntity[formKey(name)] = singleRef;
                                    if (Array.isArray(existingValue)) {
                                        const firstItem = existingValue[0];
                                        const attrId = extractReferenceId(firstItem);
                                        if (attrId) {
                                            existingValue = attrId;
                                        } else {
                                            existingValue = extractReferenceDisplayName(firstItem) || '';
                                        }
                                    } else {
                                        const attrId = extractReferenceId(existingValue);
                                        if (attrId) {
                                            existingValue = attrId;
                                        } else {
                                            existingValue = extractReferenceDisplayName(existingValue) || '';
                                        }
                                    }
                                }

                                if ((isRequestApprovalType || isPluginFunction) && isARef(attrRef, 'PossessiveActorsAttributes')) {
                                    if (Array.isArray(existingValue)) {
                                        const ids = existingValue
                                            .map((item: any) => typeof item === 'string' ? item.trim() : (extractReferenceId(item) || extractReferenceDisplayName(item) || ''))
                                            .filter((id: string) => id);
                                        existingValue = ids.join(', ');
                                    } else if (existingValue != null && existingValue !== '' && typeof existingValue !== 'string') {
                                        const attrId = extractReferenceId(existingValue) || extractReferenceDisplayName(existingValue) || '';
                                        existingValue = attrId ? String(attrId) : '';
                                    } else {
                                        existingValue = typeof existingValue === 'string' ? existingValue.trim() : '';
                                    }
                                }

                                if (isPolicyRule && isARef(attrRef, 'RelativeTargetAttribute')) {
                                    if (existingValue && typeof existingValue === 'object' && existingValue !== null) initialRefsFromEntity[formKey(name)] = existingValue;
                                    const attrId = extractReferenceId(existingValue);
                                    existingValue = attrId ? attrId : '';
                                }

                                if (isPolicyRule && isARef(attrRef, 'Attributes')) {
                                    if (existingValue === null || existingValue === undefined) {
                                        initialData['AllAttributes'] = true;
                                        existingValue = null;
                                    } else if (Array.isArray(existingValue)) {
                                        initialData['AllAttributes'] = false;
                                        existingValue = existingValue.map((attr: any) => {
                                            const id = typeof attr === 'string' ? attr : (extractReferenceId(attr) || (attr?.Id ?? attr?.id ?? attr?.DisplayName ?? ''));
                                            return typeof id === 'string' ? id : '';
                                        }).filter(Boolean);
                                    } else {
                                        initialData['AllAttributes'] = false;
                                        existingValue = [];
                                    }
                                }

                                if (isPolicyRule && (isARef(attrRef, 'RequestorSet') || isARef(attrRef, 'TargetSet') ||
                                    isARef(attrRef, 'TargetPostOpSet') || isARef(attrRef, 'RequestWorkflow') ||
                                    isARef(attrRef, 'AuthorizationWorkflow'))) {
                                    if (existingValue && typeof existingValue === 'object' && existingValue !== null) initialRefsFromEntity[formKey(name)] = existingValue;
                                    existingValue = extractReferenceId(existingValue);
                                }

                                if (isARef(attrRef, 'FilterScope') || isARef(attrRef, 'Filter Scope') || isARef(attrRef, 'PossessiveScope')) {
                                    if (Array.isArray(existingValue)) {
                                        const first = existingValue.find((v: any) => v != null && v !== '');
                                        existingValue = first != null ? String(first) : '';
                                    } else if (existingValue != null && existingValue !== '' && typeof existingValue === 'object') {
                                        existingValue = (existingValue as any).Id ?? (existingValue as any).id ?? (existingValue as any).DisplayName ?? (existingValue as any).displayName ?? '';
                                    }
                                }

                                if (attr.multiValued && attr.isReference && Array.isArray(existingValue)) {
                                    existingValue = existingValue.map((item: any) => {
                                        if (item == null) return null;
                                        if (typeof item === 'string') return { Id: item, DisplayName: item };
                                        const id = extractReferenceId(item);
                                        const display = extractReferenceDisplayName(item);
                                        const refEntityType = item?.EntityType ?? item?.entityType;
                                        if (id) return { Id: id, ...(display && { DisplayName: display }), ...(refEntityType != null && refEntityType !== '' && { EntityType: String(refEntityType).trim() }) };
                                        return null;
                                    }).filter(Boolean);
                                }

                                if (!attr.multiValued && attr.isReference && existingValue && typeof existingValue === 'object' && existingValue !== null) {
                                    initialRefsFromEntity[formKey(name)] = existingValue;
                                    const refId = extractReferenceId(existingValue);
                                    if (refId) {
                                        existingValue = refId;
                                    } else {
                                        existingValue = extractReferenceDisplayName(existingValue) || '';
                                    }
                                }

                                const key = formKey(name);
                                if (attr.type === 'boolean') {
                                    if (isPolicyRule && !isEditMode && !existingEntity && initialData[key] !== undefined) {
                                        original[key] = initialData[key];
                                    } else if (isPolicyRule && !isEditMode && !currentExistingEntity) {
                                        if (isARef(attrRef, 'AllAttributes')) {
                                            initialData[key] = true;
                                            original[key] = true;
                                        } else if (isARef(attrRef, 'ReadEntity')) {
                                            initialData[key] = true;
                                            original[key] = true;
                                        } else {
                                            initialData[key] = existingValue !== undefined && existingValue !== null ? Boolean(existingValue) : false;
                                            original[key] = existingValue !== undefined && existingValue !== null ? Boolean(existingValue) : false;
                                        }
                                    } else {
                                        initialData[key] = existingValue !== undefined && existingValue !== null ? Boolean(existingValue) : false;
                                        original[key] = existingValue !== undefined && existingValue !== null ? Boolean(existingValue) : false;
                                    }
                                } else if (existingValue !== undefined && existingValue !== null) {
                                    initialData[key] = existingValue;
                                    original[key] = existingValue;
                                } else if (attr.multiValued) {
                                    initialData[key] = [];
                                    original[key] = [];
                                } else if (nonIndexedHasLengthCountNames.has(normalizeAttrForStubMatch(name))) {
                                    initialData[key] = NON_INDEXED_PROPERTY_STUB;
                                    original[key] = NON_INDEXED_PROPERTY_STUB;
                                } else {
                                    initialData[key] = '';
                                    original[key] = '';
                                }
                            });
                        }

                        if (isPolicyRule && currentExistingEntity) {
                            if (currentExistingEntity.Attributes !== undefined && currentExistingEntity.Attributes !== null) {
                                const raw = currentExistingEntity.Attributes;
                                const arr = Array.isArray(raw)
                                    ? raw.map((a: any) => {
                                        const id = typeof a === 'string' ? a : extractReferenceId(a) || (a?.Id ?? a?.id ?? a?.DisplayName ?? '');
                                        return (id && typeof id === 'string' ? id : '') || (typeof a === 'string' ? a : '');
                                    }).filter(Boolean)
                                    : [];
                                initialData['Attributes'] = arr;
                                original['Attributes'] = arr;
                            }
                            if (currentExistingEntity.AllAttributes !== undefined) {
                                initialData['AllAttributes'] = Boolean(currentExistingEntity.AllAttributes);
                                original['AllAttributes'] = Boolean(currentExistingEntity.AllAttributes);
                            }
                        }

                        if (open) {
                            setOriginalData(original);
                        }
                    } else {
                        formAttributes.forEach((attr: any) => {
                            const name = attr.name || attr.id || attr.value;
                            const key = formKey(name);
                            if (attr.type === 'boolean') {
                                initialData[key] = false;
                            } else if (attr.multiValued) {
                                initialData[key] = [];
                            } else {
                                initialData[key] = '';
                            }
                        });

                        if (isPolicyRule) {
                            initialData['AllAttributes'] = true;
                            initialData['ReadEntity'] = true;
                        }

                        if (isGroup) {
                            initialData['Scope'] = 'Universal';
                            initialData['Type'] = 'Security';
                        }
                    }

                    if (open) {
                        if (isRequestApprovalType) {
                            const typeVal = (initialData.Type ?? initialData.type ?? '').toString().trim().toLowerCase();
                            if (typeVal !== 'possessive') {
                                initialData.PossessiveScope = '';
                                initialData.PossessiveTargetAttribute = '';
                                initialData.PossessiveActorsAttribute = '';
                                initialData.PossessiveActorsAttributes = '';
                            }
                        }
                        const isCreateMode = !isEditMode && !currentExistingEntity;
                        if (isCreateMode && wasAlreadyInitialized) {
                            hasInitializedRef.current = true;
                        } else {
                            setFormData(initialData);
                            setInitialReferenceEntitiesFromEntity(initialRefsFromEntity ?? {});

                            const newRawInputs: Record<string, string> = {};
                            formAttributes.forEach((attr: any) => {
                                if (attr.multiValued) {
                                    const name = attr.name || attr.id || attr.value;
                                    const value = initialData[formKey(name)];
                                    if (value !== undefined) {
                                        const displayValue = Array.isArray(value)
                                            ? value.map((v: any) => typeof v === 'string' ? v : (typeof v === 'object' && v !== null ? (v.Id || v.id || v.DisplayName || v.displayName || String(v)) : String(v))).join(', ')
                                            : (value || '');
                                        newRawInputs[name] = displayValue;
                                    }
                                }
                            });

                            if (Object.keys(newRawInputs).length > 0) {
                                setMultiValuedRawInputs(newRawInputs);
                            } else {
                                setMultiValuedRawInputs({});
                            }
                            hasInitializedRef.current = true;
                        }
                    }
                } catch (err) {
                    console.error('Error loading schema:', err);
                    if (open) {
                        setError('Failed to load schema attributes');
                    }
                } finally {
                    if (open) {
                        setSchemaLoading(false);
                    }
                    isInitializingRef.current = false;
                }
            };
            runInit();
        };

        if (open) {
            loadSchema();
        }
    }, [
        open,
        entityType,
        isEditMode,
        entityId,
        isRequestApprovalType,
        isContent,
        isEmailTemplate,
        isFile,
        targetEntitlement,
        tier3Settled,
        entityTypeFieldOptions,
        existingEntityShapeKey,
        existingEntityBodyKey,
        existingEntityContentTypeKey,
        isPluginFunction,
        isPolicyRule,
        isSet,
        isSetTransition,
        entityService,
        showFieldDebugInfo,
        formKeyFromAttrName,
        fetchTargetSetFilterScope,
        targetEntitlementProp,
        parentProvidesEntityLoad,
    ]);

    return { schemaAttributes, schemaLoading, schemaEntityBindings };
}
